import {
  AlertLooks,
  StandardAlert,
  StandardButton,
  StandardFieldset,
  StandardTextarea,
  StandardTextfield,
} from '@brandfolder/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { FunctionComponent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { object, SchemaOf, string } from 'yup';

import { useFetch, UseFetchOptions } from '@api/ApiHelper';
import { ApiDatumResponse } from '@api/v4/ApiResponseTypes';
import { UserGroupServer, UserGroupServerCreate } from '@api/v4/user_groups';
import { emailAddressRegex } from '@helpers/emails';

import classes from './styles/user-groups.module.scss';

interface UserGroupsFormProps {
  organizationKey: string;
  setRefetch: SetStateDispatch<boolean>;
}

interface UserGroupValues extends UserGroupServerCreate {
  emailAddresses: string;
}

const defaultValues: UserGroupValues = {
  description: '',
  emailAddresses: '',
  name: ''
};

const errorMessages = {
  emailAddressInvalid: bfTranslate('Email address is invalid'),
  emailAddressesInvalid: bfTranslate('Email addresses are invalid'),
  emailAddressRequired: bfTranslate('Email address is required'),
  nameRequired: bfTranslate('Group name is required')
};

const schema: SchemaOf<UserGroupValues> = object().shape({
  description: string().max(2000, bfTranslate('Maximum characters allowed is 2000')),
  emailAddresses: string()
    .required(errorMessages.emailAddressRequired)
    .test('emailAddress', errorMessages.emailAddressInvalid, (value): boolean => {
      // more than one email, skip
      if (value?.includes(',')) return true;
      // one email
      return emailAddressRegex.test(value);
    })
    .test('emailAddresses', errorMessages.emailAddressesInvalid, (value): boolean => {
      // one email, skip
      if (!value?.includes(',')) return true;
      // more than one email
      let allEmailsValid = true;
      const emails = value.split(',');
      for (let i = 0; i < emails.length; i++) {
        const email = emails[i];
        // if user entered test@test.com, with the dangling comma
        // the last email is blank which we can default to valid
        const isValid = email ? emailAddressRegex.test(email) : true;
        if (!isValid) {
          allEmailsValid = false;
          break;
        }
      }
      return allEmailsValid;
    }),
  name: string()
    .required(errorMessages.nameRequired)
});

export const UserGroupsForm: FunctionComponent<UserGroupsFormProps> = (props) => {
  const { organizationKey, setRefetch } = props;

  const { formState, handleSubmit, register, reset } = useForm<UserGroupValues>({
    defaultValues,
    resolver: yupResolver(schema)
  });

  const { errors, dirtyFields } = formState;
  const dirtyFieldsLength = Object.keys(dirtyFields).length;

  const fetchOptions: UseFetchOptions = {
    fetchOnMount: false,
    method: 'POST',
    url: `/api/v4/organizations/${organizationKey}/user_groups`
  };

  const {
    error,
    fetch,
    loading,
    response,
    reset: createReset
  } = useFetch<ApiDatumResponse<UserGroupServer, 'user_groups'>>(fetchOptions);

  useEffect(() => {
    if (response?.data) {
      reset();
      setRefetch(true);
    }
  }, [response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dirtyFieldsLength > 0) {
      createReset();
    }
  }, [dirtyFieldsLength]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {!error && !loading && response?.data && (
        <StandardAlert
          className={classes.alert}
          look={AlertLooks.Success}
        >
          {bfTranslate('User group created successfully!')}
        </StandardAlert>
      )}

      {error && !loading && (
        <StandardAlert
          className={classes.alert}
          look={AlertLooks.Error}
        >
          {bfTranslate('Something went wrong while creating the user group. Please try again or contact customer support.')}
        </StandardAlert>
      )}

      <form
        id="user-groups-form"
        noValidate
        onSubmit={handleSubmit((values) => {
          fetch({
            ...fetchOptions,
            body: {
              data: {
                attributes: values
              }
            }
          });
        })}
      >
        <StandardFieldset
          className={classes.fieldset}
          disabled={loading}
          legend={bfTranslate('Create a group')}
        >
          <div className={classes.grid}>
            <StandardTextarea
              {...register('emailAddresses')}
              className={classes.textarea}
              disabled={loading}
              error={errors.emailAddresses?.message}
              id="emailAddresses"
              label={
                <>
                  {bfTranslate('Email addresses')}&nbsp;
                  <small className={classes.small}>
                    ({bfTranslate('comma separated')})
                  </small>
                </>
              }
              required
              requiredLabel={bfTranslate('Required')}
            />
            <StandardTextarea
              {...register('description')}
              className={classes.textarea}
              disabled={loading}
              error={errors.description?.message}
              id="description"
              label={
                <>
                  {bfTranslate('Description')}&nbsp;
                  <small className={classes.small}>
                    ({bfTranslate('optional')})
                  </small>
                </>
              }
              maxLength={2000}
            />
          </div>
          <div className={classes.grid}>
            <StandardTextfield
              {...register('name')}
              className={classes.textfield}
              disabled={loading}
              error={errors.name?.message}
              id="name"
              label={bfTranslate('Group name')}
              required
              requiredLabel={bfTranslate('Required')}
            />
            <StandardButton
              className={classes.button}
              disabled={loading}
              id="create-group"
              type="submit"
            >
              {bfTranslate('Create group')}
            </StandardButton>
          </div>
        </StandardFieldset>
      </form>
    </>
  );
};
